import { isValidCedula, isValidPasaporte } from "../../../utils/ValidationUtil";
import {
  HOSPITALIZACION_CONSTANT_VALUES,
  HOSPITALIZACION_ERRORS,
  MODALIDADES_PSS,
} from "./HospitalizacionConstant";

const validarDocumento = (tipoDocumento, documento) => {
  if (!tipoDocumento || !documento) return null;

  if (tipoDocumento === HOSPITALIZACION_CONSTANT_VALUES.tipoDocumento.CEDULA) {
    if (!isValidCedula(documento)) {
      return { documento: HOSPITALIZACION_ERRORS.documento.CEDULA };
    }
  }

  if (
    tipoDocumento === HOSPITALIZACION_CONSTANT_VALUES.tipoDocumento.PASAPORTE
  ) {
    if (!isValidPasaporte(documento)) {
      return { documento: HOSPITALIZACION_ERRORS.documento.PASAPORTE };
    }
  }

  return null;
};

const validarRangoFechas = (fechaIngreso, fechaSalida) => {
  if (!fechaIngreso) return null;

  if (!fechaSalida) {
    return {
      fechaSalida:
        "La fecha de egreso es obligatoria cuando se selecciona fecha de ingreso",
    };
  }

  const fechaIngresoDate = new Date(fechaIngreso);
  const fechaSalidaDate = new Date(fechaSalida);

  if (fechaSalidaDate < fechaIngresoDate) {
    return {
      fechaSalida:
        "La fecha de egreso no puede ser menor que la fecha de ingreso",
    };
  }

  return null;
};

export const ValidarFormularioFiltrosPacientesIngresados = (formData) => {
  if (!formData) return [];

  const errors = [];

  const errorDocumento = validarDocumento(
    formData.tipoDocumento,
    formData.documento
  );
  if (errorDocumento) {
    errors.push(errorDocumento);
  }

  const errorFechas = validarRangoFechas(
    formData.fechaIngreso,
    formData.fechaSalida
  );
  if (errorFechas) {
    errors.push(errorFechas);
  }

  return errors;
};

const validarCoberturaCompleta = (cobertura, modalidadPss) => {
  if (!cobertura.cobertura) return true;

  if (modalidadPss === MODALIDADES_PSS.ARS) {
    return (
      !cobertura.medico ||
      !cobertura.medico.codigo ||
      !cobertura.medico.nombre ||
      !cobertura.medico.especialidad
    );
  }

  if (modalidadPss === MODALIDADES_PSS.PAQUETE) {
    return (
      !cobertura.medico ||
      !cobertura.medico.nombre ||
      !cobertura.medico.especialidad ||
      !cobertura.medico.codigoEspecialidad
    );
  }

  return true;
};

const validarCoberturasCompletas = (coberturasMedicos) => {
  const coberturasIncompletas = coberturasMedicos.filter((item) =>
    validarCoberturaCompleta(item, item.modalidadPss)
  );

  if (coberturasIncompletas.length > 0) {
    // Verificar si hay coberturas en modalidad PAQUETE sin especialidad
    const coberturasPaqueteSinEspecialidad = coberturasIncompletas.filter(
      (item) =>
        item.modalidadPss === MODALIDADES_PSS.PAQUETE &&
        item.cobertura &&
        (!item.medico?.especialidad || !item.medico?.codigoEspecialidad)
    );

    if (coberturasPaqueteSinEspecialidad.length > 0) {
      return {
        type: "error",
        title: "Especialidad Requerida",
        message:
          "En modalidad PAQUETE, todas las coberturas deben tener una especialidad seleccionada.",
        field: "coberturasMedicos",
      };
    }

    return {
      type: "error",
      title: "Coberturas Incompletas",
      message:
        "Todas las coberturas deben tener todos los campos completos según su modalidad.",
      field: "coberturasMedicos",
    };
  }

  return null;
};

const validarLimiteMedicosARS = (coberturasMedicos, modalidadPss) => {
  if (modalidadPss !== MODALIDADES_PSS.ARS) return null;

  const medicosUnicos = new Set();
  coberturasMedicos.forEach((item) => {
    if (item.medico?.codigo) {
      medicosUnicos.add(item.medico.codigo);
    }
  });

  if (medicosUnicos.size > 5) {
    return {
      type: "error",
      title: "Límite de Médicos Excedido",
      message: "No puede agregar más de 5 médicos diferentes en modalidad ARS.",
      field: "coberturasMedicos",
    };
  }

  return null;
};

export const validarFormularioHojaQuirurgica = (hojaData) => {
  if (!hojaData) {
    return {
      isValid: false,
      errors: [
        {
          type: "error",
          title: "Datos Inválidos",
          message: "Los datos del formulario son requeridos.",
        },
      ],
    };
  }

  const errors = [];
  const {
    attachedFiles = [],
    coberturasMedicos = [],
    modalidadPss,
  } = hojaData;

  if (coberturasMedicos.length === 0) {
    errors.push({
      type: "error",
      title: "Coberturas con Médicos Requeridas",
      message: "Debe agregar al menos una cobertura con médico participante.",
      field: "coberturasMedicos",
    });
  }

  if (coberturasMedicos.length > 0) {
    const errorCoberturasIncompletas = validarCoberturasCompletas(
      coberturasMedicos
    );
    if (errorCoberturasIncompletas) {
      errors.push(errorCoberturasIncompletas);
    }

    const errorLimiteMedicos = validarLimiteMedicosARS(
      coberturasMedicos,
      modalidadPss
    );
    if (errorLimiteMedicos) {
      errors.push(errorLimiteMedicos);
    }
  }

  if (attachedFiles.length === 0) {
    errors.push({
      type: "error",
      title: "Documento Requerido",
      message:
        "Debe agregar al menos 1 documento para poder guardar la hoja quirúrgica.",
      field: "attachedFiles",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validarCorreo = (email) => {
  if (!email || email.trim() === "") {
    return "El correo electrónico es obligatorio";
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return "El correo electrónico no tiene un formato válido";
  }
  return "";
};
